/**
 * Hooks para a tela de monitoramento do Pipeline KB (admin/root).
 * Todas as leituras são feitas via RPCs `kb_admin_*` (SECURITY DEFINER).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { toast } from "sonner";

const REFRESH_MS = 15_000;

export interface KbOverviewRow {
  queue_name: string;
  scope: string; // "__total__" para linha da fila; senão scope real
  pending: number;
  processing: number;
  failed: number;
  dlq_total: number;
}

export function useKbPipelineOverview() {
  return useQuery({
    queryKey: ["kb-admin", "overview"],
    queryFn: async (): Promise<KbOverviewRow[]> => {
      const { data, error } = await supabase.rpc("kb_admin_pipeline_overview" as any);
      if (error) {
        logger.error("kb-admin", "overview error", error);
        throw error;
      }
      return (data ?? []) as KbOverviewRow[];
    },
    refetchInterval: REFRESH_MS,
  });
}

export interface KbVolumeRow {
  bucket_at: string;
  scope: string;
  created_count: number;
  done_count: number;
  failed_count: number;
}

export function useKbPipelineVolume(bucketMinutes = 60) {
  return useQuery({
    queryKey: ["kb-admin", "volume", bucketMinutes],
    queryFn: async (): Promise<KbVolumeRow[]> => {
      const { data, error } = await supabase.rpc("kb_admin_pipeline_volume_24h" as any, {
        p_bucket_minutes: bucketMinutes,
      });
      if (error) throw error;
      return (data ?? []) as KbVolumeRow[];
    },
    refetchInterval: REFRESH_MS,
  });
}

export interface KbTenantRow {
  tenant_id: string;
  tenant_name: string;
  published_pages: number;
  total_chunks: number;
  total_embeddings: number;
  jobs_pending: number;
  jobs_processing: number;
  jobs_failed: number;
  dlq_total: number;
  total_count: number;
}

export function useKbPipelineByTenant(params: {
  search?: string;
  page: number;
  pageSize: number;
}) {
  const { search, page, pageSize } = params;
  return useQuery({
    queryKey: ["kb-admin", "by-tenant", search, page, pageSize],
    queryFn: async (): Promise<KbTenantRow[]> => {
      const { data, error } = await supabase.rpc("kb_admin_pipeline_by_tenant" as any, {
        p_search: search || null,
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
      });
      if (error) throw error;
      return (data ?? []) as KbTenantRow[];
    },
    refetchInterval: REFRESH_MS,
  });
}

export interface KbPageRow {
  page_id: string;
  page_title: string;
  page_slug: string;
  space_id: string | null;
  space_name: string | null;
  page_status: string;
  chunks_count: number;
  embeddings_count: number;
  jobs_pending: number;
  jobs_processing: number;
  jobs_failed: number;
  last_error: string | null;
  updated_at: string;
  total_count: number;
}

export function useKbPipelineByPage(params: {
  tenantId: string | null;
  search?: string;
  status?: string | null;
  page: number;
  pageSize: number;
}) {
  const { tenantId, search, status, page, pageSize } = params;
  return useQuery({
    queryKey: ["kb-admin", "by-page", tenantId, search, status, page, pageSize],
    enabled: !!tenantId,
    queryFn: async (): Promise<KbPageRow[]> => {
      const { data, error } = await supabase.rpc("kb_admin_pipeline_by_page" as any, {
        p_tenant_id: tenantId,
        p_search: search || null,
        p_status: status || null,
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
      });
      if (error) throw error;
      return (data ?? []) as KbPageRow[];
    },
    refetchInterval: REFRESH_MS,
  });
}

export function useKbPipelinePageDetail(pageId: string | null) {
  return useQuery({
    queryKey: ["kb-admin", "page-detail", pageId],
    enabled: !!pageId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("kb_admin_pipeline_page_detail" as any, {
        p_page_id: pageId,
      });
      if (error) throw error;
      return data as any;
    },
    refetchInterval: REFRESH_MS,
  });
}

export interface KbDlqRow {
  dlq_id: string;
  queue_name: string;
  scope: string;
  tenant_id: string | null;
  tenant_name: string | null;
  page_id: string | null;
  page_title: string | null;
  reason: string | null;
  last_error: string | null;
  attempts: number;
  moved_at: string;
  total_count: number;
}

export function useKbPipelineDlq(params: {
  queue?: string | null;
  tenantId?: string | null;
  page: number;
  pageSize: number;
}) {
  const { queue, tenantId, page, pageSize } = params;
  return useQuery({
    queryKey: ["kb-admin", "dlq", queue, tenantId, page, pageSize],
    queryFn: async (): Promise<KbDlqRow[]> => {
      const { data, error } = await supabase.rpc("kb_admin_pipeline_dlq_list" as any, {
        p_queue: queue || null,
        p_tenant_id: tenantId || null,
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
      });
      if (error) throw error;
      return (data ?? []) as KbDlqRow[];
    },
    refetchInterval: REFRESH_MS,
  });
}

export function useKbReprocessDlqMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dlqId: string) => {
      const { data, error } = await supabase.rpc("kb_admin_pipeline_reprocess_dlq" as any, {
        p_dlq_id: dlqId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      toast.success("Item reenfileirado");
      qc.invalidateQueries({ queryKey: ["kb-admin"] });
    },
    onError: (e: any) => {
      logger.error("kb-admin", "reprocess dlq error", e);
      toast.error(e?.message ?? "Falha ao reprocessar");
    },
  });
}

export function useKbPurgeFailedMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { queue: string; olderThanHours: number }) => {
      const { data, error } = await supabase.rpc("kb_admin_pipeline_purge_failed" as any, {
        p_queue: params.queue,
        p_older_than_hours: params.olderThanHours,
      });
      if (error) throw error;
      return (data as number) ?? 0;
    },
    onSuccess: (n) => {
      toast.success(`${n} job(s) failed removidos`);
      qc.invalidateQueries({ queryKey: ["kb-admin"] });
    },
    onError: (e: any) => {
      logger.error("kb-admin", "purge failed error", e);
      toast.error(e?.message ?? "Falha ao limpar");
    },
  });
}
